import _ from 'lodash';

/**
 * Executes an array of functions in sequence with a specified interval between each execution.
 * The first function is executed immediately, and subsequent functions are executed at the specified interval.
 *
 * @param {VoidFunction[]} functions - An array of functions to be executed in sequence.
 * @param {number} [interval=1000] - The interval in milliseconds between each function execution.
 * 
 * Example usage:
 * You’re building a tutorial walkthrough for a web app. Each step highlights a different UI component with a short delay between highlights to give users time to absorb the information.

    You can use this function to sequentially execute highlight animations:
    
    const highlightStep1 = () => highlightElement('#navbar');
    const highlightStep2 = () => highlightElement('#sidebar');
    const highlightStep3 = () => highlightElement('#main-content');

    executeInSequence([highlightStep1, highlightStep2, highlightStep3], 2000);
 */

type VoidFunction = () => void;

function runInSequenceWithInterval(
  functions: VoidFunction[],
  interval: number = 1000
): void {
  if (_.isEmpty(functions)) return;

  let index = 0;

  // Execute the first function immediately
  const first = functions[index];
  if (_.isFunction(first)) {
    first();
  }

  index++;

  const intervalId = setInterval(() => {
    if (index >= functions.length) {
      clearInterval(intervalId);
      return;
    }

    const fn = functions[index];
    if (_.isFunction(fn)) {
      fn();
    }

    index++;
  }, interval);
}



export default runInSequenceWithInterval;